import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Member } from "~/store";
import { toggleMemberAdminStatus } from "../api/groups";
import { createMember, deleteMember, updateMember } from "../api/members";

/**
 * Custom hook for group member mutations that handles cache invalidation
 * @returns Object containing mutation functions for adding, removing, and updating members
 */
export function useGroupMembersMutations() {
  const queryClient = useQueryClient();

  // Create member mutation
  const createMemberMutation = useMutation({
    mutationFn: (memberData: {
      name: string;
      birthdate: string;
      gender: "M" | "F" | "O";
      email?: string;
      profileImageUrl?: string;
      groupId: number;
      isUserMember?: boolean;
    }) => createMember(memberData),
    onSuccess: (_, variables) => {
      // Invalidate group members cache when a member is added
      if (variables.groupId) {
        queryClient.invalidateQueries({ queryKey: ["groupMembers", variables.groupId] });
      }
      // Also invalidate all members cache
      queryClient.invalidateQueries({ queryKey: ["members"] });
    }
  });

  // Update member mutation
  const updateMemberMutation = useMutation({
    mutationFn: ({ memberId, data }: { memberId: number; data: Partial<Member> }) => updateMember(memberId, data),
    onSuccess: () => {
      // Invalidate all member queries when a member is updated
      queryClient.invalidateQueries({ queryKey: ["members"] });
      // Also invalidate all group members queries since the member might be in multiple groups
      queryClient.invalidateQueries({ queryKey: ["groupMembers"] });
    }
  });

  // Toggle member admin status mutation
  const toggleAdminStatusMutation = useMutation({
    mutationFn: ({ groupId, memberId }: { groupId: number; memberId: number }) =>
      toggleMemberAdminStatus(groupId, memberId),
    onSuccess: (_, variables) => {
      // Invalidate group members cache when a member's role is updated
      queryClient.invalidateQueries({ queryKey: ["groupMembers", variables.groupId] });
    }
  });

  // Delete member mutation
  const deleteMemberMutation = useMutation({
    mutationFn: (memberId: number) => deleteMember(memberId),
    onSuccess: () => {
      // Invalidate all member queries when a member is deleted
      queryClient.invalidateQueries({ queryKey: ["members"] });
      // Also invalidate all group members queries since the member might be in multiple groups
      queryClient.invalidateQueries({ queryKey: ["groupMembers"] });
    }
  });

  return {
    createMemberMutation,
    updateMemberMutation,
    toggleAdminStatusMutation,
    deleteMemberMutation
  };
}
